import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { SeoHeader } from '@/components/SeoHeader';
import { Layout } from '@/components/layout';
import Hero from '@/sections/Hero';
import { getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import React from 'react';
import { Advantages } from '@/sections/career/Advantages';
import { Career as CareerSection } from '@/sections/career/Career';
import { Facts } from '@/sections/career/Facts';
import { Faq } from '@/sections/career/Faq';
import { HowItWorks } from '@/sections/career/HowItWorks';
import { Intro } from '@/sections/career/Intro';
import { MoreInfo } from '@/sections/career/MoreInfo';
import { Testimonials } from '@/sections/Testimonials';
import { Job } from '@/types/job';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { getTestimonialsBySlug, Testimonial } from '@/api/sanity/testimonials';
import { EXTERNAL_LINKS } from '@/constants/links';
import { Button } from '@/components/buttons';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { getJobs } from '@/sections/jobs/functions/getJobs';
import { isAllowedRoute } from '@/lang/pagesConfig';
interface Props {
  pageBanner: PageBanner | null;
  jobs: Job[];
  jobsCount: string;
  testimonials: Testimonial[];
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Career(t0) {
  const $ = _c(64);
  const {
    jobs,
    jobsCount,
    pageBanner,
    testimonials,
    menuPromoBanners
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["career", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  usePageView("career");
  let t2;
  if ($[1] !== t) {
    t2 = t("seo.title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("seo.description");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== pageBanner?.title || $[9] !== t) {
    t5 = pageBanner?.title ?? t("banner.title");
    $[8] = pageBanner?.title;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== pageBanner?.text || $[12] !== t) {
    t6 = pageBanner?.text ?? t("banner.text");
    $[11] = pageBanner?.text;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = pageBanner ? pageBanner.buttons : null;
  const t8 = pageBanner?.image?.url ?? "/images/sections/career/hero/hero.png";
  let t9;
  if ($[14] !== pageBanner?.image?.alt || $[15] !== t) {
    t9 = pageBanner?.image?.alt ?? t("banner.title");
    $[14] = pageBanner?.image?.alt;
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = {
      url: t8,
      alt: t9
    };
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = pageBanner?.variant || "video";
  const t12 = pageBanner ? pageBanner.videoId : "VggNOGNSdSg";
  let t13;
  if ($[20] !== t10 || $[21] !== t11 || $[22] !== t12 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
    t13 = <Hero title={t5} text={t6} buttons={t7} image={t10} variant={t11} videoId={t12} />;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-700" background="bg-transparent" /></div>;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="bgCircle absolute bottom-32 left-1/2 hidden h-[27rem] w-[27rem] translate-x-1/2 lg:block" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== jobs || $[30] !== jobsCount) {
    t16 = <div className="relative z-30 bg-blue-700 pt-10 pb-20 lg:pt-20 lg:pb-40">{t15}<CareerSection jobs={jobs} jobsCount={jobsCount} /></div>;
    $[29] = jobs;
    $[30] = jobsCount;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SectionDelimiter color="bg-blue-900" background="bg-blue-700" />;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="bgCircle absolute top-2/3 left-1/2 hidden h-[25rem] w-[25rem] translate-x-1/2 -translate-y-1/2 lg:block" />;
    t19 = <div className="bgCircle absolute top-1/3 left-0 -translate-x-1/2 -translate-y-1/2 lg:top-1/4" />;
    $[33] = t18;
    $[34] = t19;
  } else {
    t18 = $[33];
    t19 = $[34];
  }
  let t20;
  let t21;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="relative bg-blue-900 pt-10 pb-20 lg:pt-20 lg:pb-40">{t18}{t19}<div className="relative z-10 flex flex-col gap-20 lg:gap-40"><Intro /><Facts /></div></div>;
    t21 = <SectionDelimiter color="bg-blue-100" background="bg-blue-900" />;
    $[35] = t20;
    $[36] = t21;
  } else {
    t20 = $[35];
    t21 = $[36];
  }
  let t22;
  let t23;
  let t24;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="bgCircle absolute top-2/3 left-0 -translate-x-1/4 -translate-y-1/2 lg:top-1/2" />;
    t23 = <div className="bgCircle absolute top-0 right-0 translate-x-1/2 translate-y-1/2 lg:hidden" />;
    t24 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 -translate-y-1/4 lg:hidden" />;
    $[37] = t22;
    $[38] = t23;
    $[39] = t24;
  } else {
    t22 = $[37];
    t23 = $[38];
    t24 = $[39];
  }
  let t25;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Advantages />;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== t) {
    t26 = t("testimonials.title");
    $[41] = t;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = EXTERNAL_LINKS();
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  const t28 = t27.atmoskop;
  let t29;
  if ($[44] !== t) {
    t29 = t("testimonials.cta");
    $[44] = t;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] !== t29) {
    t30 = <Button variant="secondary" testId="atmoskopTestimonials" analyticsParam="atmoskopTestimonials" to={t28} size="lg" align="center" target="_blank">{t29}</Button>;
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== t26 || $[49] !== t30 || $[50] !== testimonials) {
    t31 = <div className="relative overflow-x-hidden bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40">{t22}{t23}{t24}<div className="relative z-10 flex flex-col gap-20 lg:gap-40">{t25}<Testimonials title={t26} testimonials={testimonials} buttons={t30} /></div></div>;
    $[48] = t26;
    $[49] = t30;
    $[50] = testimonials;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== t13 || $[53] !== t16 || $[54] !== t31) {
    t32 = <div className="relative overflow-x-hidden">{t13}{t14}{t16}{t17}{t20}{t21}{t31}</div>;
    $[52] = t13;
    $[53] = t16;
    $[54] = t31;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <SectionDelimiter color="bg-blue-700" background="bg-blue-100" />;
    $[56] = t33;
  } else {
    t33 = $[56];
  }
  let t34;
  let t35;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="relative bg-blue-700 pt-10 pb-20 lg:pt-20 lg:pb-40"><HowItWorks /></div>;
    t35 = <SectionDelimiter color="bg-blue-50" background="bg-blue-700" />;
    $[57] = t34;
    $[58] = t35;
  } else {
    t34 = $[57];
    t35 = $[58];
  }
  let t36;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div className="relative overflow-hidden bg-blue-50 pt-10 pb-14 lg:pt-20 lg:pb-26"><div className="relative z-10 flex flex-col gap-20 lg:gap-40"><Faq /><MoreInfo /></div><div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" /></div>;
    $[59] = t36;
  } else {
    t36 = $[59];
  }
  let t37;
  if ($[60] !== menuPromoBanners || $[61] !== t32 || $[62] !== t4) {
    t37 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t32}{t33}{t34}{t35}{t36}</Layout>;
    $[60] = menuPromoBanners;
    $[61] = t32;
    $[62] = t4;
    $[63] = t37;
  } else {
    t37 = $[63];
  }
  return t37;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('kariera');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  const {
    jobs,
    total
  } = await getJobs();
  const testimonials = await getTestimonialsBySlug('career');
  return {
    props: {
      pageBanner: await getPageBannerBySlug('career'),
      testimonials,
      jobsCount: total,
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      jobs,
      ...(await getIntlProps(ctx, ['core', 'career', 'testimonials']))
    }
  };
}) satisfies GetStaticProps<Props>;